<template>
  <div class="week-block">
    <div class="week-title-row">
      <h4 class="week-title">Week {{ weekIndex + 1 }}</h4>
      <button
        v-if="isPrimary"
        type="button"
        class="btn-add-inline"
        title="Add a timeblock to this week"
        @click="$emit('add-event', { weekIndex })"
      >+ Add Timeblock</button>
    </div>
    <div class="week-grid" @dblclick="onGridDblClick">
      <div class="corner"></div>
      <div v-for="d in DAY_HEADERS" :key="d.key" class="day-header">{{ d.short }}</div>

      <div v-for="h in HOURS" :key="`hr-${h}`" class="hour-label">{{ formatHourLabel(h) }}</div>

      <!-- Empty day cells for visual grid lines. 24 rows × 7 columns = 168 cells. -->
      <div
        v-for="cell in 168"
        :key="`cell-${cell}`"
        class="day-cell"
        :class="{ 'even-row': Math.floor((cell - 1) / 7) % 2 === 0 }"
      ></div>

      <!-- Rendered events on top of the grid. Each segment = one grid item.
           When multiple events overlap on the same day, they render in "lanes"
           side-by-side within the day column (Google Calendar-style). -->
      <div
        v-for="seg in segments"
        :key="seg.key"
        class="event-block"
        :class="{ 'partner-a': seg.partner === 'A', 'partner-b': seg.partner === 'B', 'multi-lane': seg.laneCount > 1 }"
        :style="{
          gridColumn: `${seg.dayCol + 2} / span 1`,
          gridRow: `${Math.floor(seg.startMin / 60) + 2} / ${Math.max(Math.floor(seg.startMin / 60) + 1, Math.ceil(seg.endMin / 60)) + 2}`,
          ...(seg.laneCount > 1 ? {
            width: `calc(${100 / seg.laneCount}% - 2px)`,
            marginLeft: `${seg.lane * 100 / seg.laneCount}%`,
          } : {}),
        }"
        :title="seg.title"
        :data-key="seg.key"
        @click="onBlockClick($event, seg)"
      >
        <!-- Top drag handle: only on the first segment of an event (owns the startTime). -->
        <span
          v-if="seg.isFirst && isPrimary"
          class="drag-handle drag-handle-top"
          title="Drag to change start time"
          @pointerdown.stop.prevent="startResize($event, seg, 'start')"
          @click.stop
        ></span>
        <div v-if="seg.isFirst" class="event-label">{{ seg.headLabel }}</div>
        <!-- Bottom drag handle: only on the last segment of an event (owns the endTime). -->
        <span
          v-if="seg.isLast && isPrimary"
          class="drag-handle drag-handle-bottom"
          title="Drag to change end time"
          @pointerdown.stop.prevent="startResize($event, seg, 'end')"
          @click.stop
        ></span>
      </div>
    </div>

    <!-- Time split for this displayed week (Sun through Sat as drawn). -->
    <div v-if="distribution && (distribution.A || distribution.B)" class="week-dist">
      <div class="dist-bar">
        <span class="dist-a" :style="{ width: weekPct(distribution.A) + '%' }"></span>
        <span class="dist-b" :style="{ width: weekPct(distribution.B) + '%' }"></span>
        <span v-if="distribution.unassigned" class="dist-gap" :style="{ width: weekPct(distribution.unassigned) + '%' }"></span>
      </div>
      <p class="week-dist-legend">
        <span><strong>{{ partyAName || 'Party A' }}</strong> {{ fmtDur(distribution.A) }} ({{ fmtPct(distribution.A) }}%)</span>
        <span><strong>{{ partyBName || 'Party B' }}</strong> {{ fmtDur(distribution.B) }} ({{ fmtPct(distribution.B) }}%)</span>
        <span v-if="distribution.unassigned" class="dist-unassigned-note">Unassigned {{ fmtDur(distribution.unassigned) }} ({{ fmtPct(distribution.unassigned) }}%)</span>
      </p>
    </div>

    <!-- Summaries are grouped by DISPLAYED row: each sentence sits under the
         week whose grid shows the block's start, and week numbers match the
         rows on screen (a block ending on the Sunday drawn at the head of
         Week 2 reads "until Sunday ... in Week 2"). -->
    <ul v-if="summaryEvents.length" class="event-summary">
      <li v-for="item in summaryEvents" :key="`${item.wi}:${item.ei}`">
        <span class="summary-body">
          <strong>{{ partnerLabel(item.e.partner) }}</strong> will have parenting time from
          {{ formatDay(item.e.startDay) }} at {{ formatTime(item.e.startTime) }} in
          Week {{ weekIndex + 1 }} until {{ formatDay(runLast(item).endDay) }} at {{ formatTime(runLast(item).endTime) }}<template v-if="runEndVisualWeek(item) !== weekIndex + 1"> in Week {{ runEndVisualWeek(item) }}</template>.
        </span>
        <span v-if="runLast(item).transition" class="transition-note">
          <strong>Transition at the end of parenting time:</strong>
          {{ transitionSentence(runLast(item)) }}<template v-if="runLast(item).transitionDetails">. {{ runLast(item).transitionDetails }}</template>
        </span>
      </li>
    </ul>
    <p v-else-if="isPrimary" class="empty-week">No timeblocks yet — double-click the grid where the time should start, click + Add Timeblock, or apply a template.</p>
    <p v-else class="empty-week">No timeblocks yet.</p>
  </div>
</template>

<script>
import { formatDurationShort, formatPercent } from '@/lib/parentingSchedule/timeDistribution'
import { childrenSubject } from '@/lib/parentingSchedule/children'
import {
  DAYS_OF_WEEK,
  MIN_PER_DAY,
  MIN_PER_WEEK,
  timeToMin,
} from '@/lib/parentingSchedule/weekMath'
import { buildSegments, collectSummaryEvents } from '@/lib/parentingSchedule/weekGeometry.js'

const DAY_HEADERS = [
  { key: 'sun', short: 'Sun' },
  { key: 'mon', short: 'Mon' },
  { key: 'tue', short: 'Tue' },
  { key: 'wed', short: 'Wed' },
  { key: 'thu', short: 'Thu' },
  { key: 'fri', short: 'Fri' },
  { key: 'sat', short: 'Sat' },
]
const HOURS = Array.from({ length: 24 }, (_, i) => i)

const DAY_LONG = { sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday' }

// Convert a visual-slot position (minutes relative to this row's data-week
// Monday 00:00; the leading Sunday is negative) to { off, day, time } in the
// data model's Monday-anchored terms. off is -1, 0, or 1.
function slotToDataPos(pos) {
  const off = Math.floor(pos / MIN_PER_WEEK)
  const rem = pos - off * MIN_PER_WEEK
  const m = rem % MIN_PER_DAY
  return {
    off,
    day: DAYS_OF_WEEK[Math.floor(rem / MIN_PER_DAY)],
    time: `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`,
  }
}

export default {
  name: 'WeekGrid',
  props: {
    weekIndex: { type: Number, required: true },
    allWeeks: { type: Array, default: () => [] }, // full rotation.weeks — every row needs cross-week context
    // Minutes for this displayed row: { A, B, unassigned }. Computed by the
    // builder via schedule-core's computeTimeDistribution.
    distribution: { type: Object, default: null },
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    children: { type: Array, default: () => [] },
    jurisdiction: { type: String, default: 'BC' },
    // 'secondary' is the non-drafting party: they can read the schedule and
    // open a block to see its details, but not add, move, resize or delete.
    role: { type: String, default: 'primary' },
    totalWeeks: { type: Number, default: 2 },
  },
  data() {
    return {
      DAY_HEADERS,
      HOURS,
      // Resize-drag state. Set on pointerdown, cleared on pointerup.
      drag: null,
      // A pointerup that follows a real drag suppresses the subsequent click.
      suppressNextClick: false,
    }
  },
  computed: {
    isPrimary() { return this.role === 'primary' },
    // Events whose blocks BEGIN in this displayed row, in chronological order.
    // A block starting on a Sunday is drawn at the head of the row AFTER its
    // data week, so its summary lists there too.
    summaryEvents() {
      return collectSummaryEvents({
        allWeeks: this.allWeeks,
        weekIndex: this.weekIndex,
        totalWeeks: this.totalWeeks,
      })
    },
    childrenLabel() {
      // "the Children" (the Agreement's defined term) when two or more
      // minors, a single minor by first name, "the children" when none are
      // passed. Children at or past the jurisdiction's age of majority drop
      // out of the wording.
      return childrenSubject(this.children, this.jurisdiction)
    },
    segments() {
      return buildSegments({
        allWeeks: this.allWeeks,
        weekIndex: this.weekIndex,
        totalWeeks: this.totalWeeks,
        // Needs partyAName / partyBName and the 12-hour formatter, so it stays here.
        headLabel: (e, rowSpan, info) => this._headLabel(e, rowSpan, info),
      })
    },
  },
  methods: {
    // Label for an event's first segment. Blocks render true to their times,
    // so short ones can't fit the full "Jane Monday 2:00 pm to Friday 2:00 pm"
    // text — they defer to the summary sentences under the calendar instead.
    _headLabel(e, rowSpan, info) {
      const name = this.partnerLabel(e.partner)
      // A block that merely continues the same parent's previous block is
      // not a new period; say so instead of restating times.
      if (info && info.isContinuation) return rowSpan >= 2 ? `${name} (continued)` : name
      // The head of a multi-block run is labelled with the whole run's span.
      const last = info && info.run.blocks.length > 1 ? info.run.last.e : e
      if (rowSpan >= 5) {
        return `${name} ${DAY_LONG[e.startDay]} ${this.formatTime(e.startTime)} to ${DAY_LONG[last.endDay]} ${this.formatTime(last.endTime)}`
      }
      if (rowSpan >= 2) return `${name} (see details below)`
      return name
    },

    // --- Double-click on an empty grid area: add a timeblock at that spot. ---
    // Computes the visual column and time from the click position, converts
    // to the data model's (weekOffset, day) pair, and asks the builder to
    // open the Add Timeblock modal pre-filled with a 2-hour block. The
    // Sunday column maps to the PREVIOUS data week (offset -1) — the modal's
    // re-homing save puts the event in the right week's events[].
    onGridDblClick(evt) {
      const rect = evt.currentTarget.getBoundingClientRect()
      const x = evt.clientX - rect.left - 44   // hour-label column is 44px
      const y = evt.clientY - rect.top - 26    // day-header row is 26px
      if (x < 0 || y < 0) return
      const col = Math.min(6, Math.floor(x / ((rect.width - 44) / 7))) // 0=Sun … 6=Sat
      const clickedMin = Math.max(0, Math.min(1410, Math.round((y / 22) * 60 / 30) * 30))
      // Visual slot in minutes relative to this row's data-week Monday 00:00.
      const startPos = (col === 0 ? -MIN_PER_DAY : (col - 1) * MIN_PER_DAY) + clickedMin
      const endPos = startPos + 120 // default 2-hour visit/block
      const start = slotToDataPos(startPos)
      const end = slotToDataPos(endPos)
      const at = {
        weekIndex: this.weekIndex,
        startWeekOffset: start.off,
        startDay: start.day,
        startTime: start.time,
        endWeekOffset: end.off,
        endDay: end.day,
        endTime: end.time,
      }

      // Double-click ON a block behaves exactly like a single click: open the
      // edit modal. (Visits/date nights are added via the button inside the
      // edit modal — one gesture to remember, nothing hidden behind
      // double-click.)
      const blockEl = evt.target.closest('.event-block')
      if (blockEl) {
        clearTimeout(this._clickTimer) // replace the pending delayed click
        const seg = this.segments.find((sg) => sg.key === blockEl.getAttribute('data-key'))
        if (seg) this.$emit('edit-event', { weekIndex: seg.sourceWeekIndex, eventIndex: seg.eventIndex })
        return
      }

      // Double-click on empty grid: plain add at the clicked spot.
      if (!this.isPrimary) return
      this.$emit('add-event-at', at)
    },

    // --- Click handler on event body: open edit modal, but only if not just-dragged. ---
    // Slightly delayed so a double-click (split / date night) can cancel it.
    onBlockClick(evt, seg) {
      if (this.suppressNextClick) { this.suppressNextClick = false; return }
      clearTimeout(this._clickTimer)
      this._clickTimer = setTimeout(() => {
        this.$emit('edit-event', { weekIndex: seg.sourceWeekIndex, eventIndex: seg.eventIndex })
      }, 250)
    },

    // --- Drag-to-resize: adjust startTime or endTime by dragging top/bottom edge. ---
    // Row height = 22px per hour → 22/60 = 0.367 px per minute. We snap to 15-min steps.
    startResize(evt, seg, edge) {
      if (!this.isPrimary) return
      // Look up the current time on the underlying event.
      // `allWeeks` is the same array the parent binds (ParentingBuilder :allWeeks="rotation.weeks"),
      // so this reads identically without reaching up through $parent.
      const week = this.allWeeks?.[seg.sourceWeekIndex]
      const event = week?.events?.[seg.eventIndex]
      if (!event) return
      const origHHMM = edge === 'start' ? event.startTime : event.endTime
      this.drag = {
        edge,
        seg,
        startY: evt.clientY,
        origMin: timeToMin(origHHMM),
        moved: false,
      }
      window.addEventListener('pointermove', this.onResizeMove)
      window.addEventListener('pointerup', this.onResizeUp)
    },
    onResizeMove(evt) {
      if (!this.drag) return
      const deltaPx = evt.clientY - this.drag.startY
      const deltaMin = Math.round(deltaPx / (22 / 60) / 15) * 15  // snap to 15 min
      if (deltaMin === 0) return
      if (!this.drag.moved && Math.abs(deltaPx) > 3) this.drag.moved = true
      let newMin = this.drag.origMin + deltaMin
      newMin = Math.max(0, Math.min(1425, newMin))  // clamp within 00:00 – 23:45
      const hh = String(Math.floor(newMin / 60)).padStart(2, '0')
      const mm = String(newMin % 60).padStart(2, '0')
      this.$emit('resize-event', {
        weekIndex: this.drag.seg.sourceWeekIndex,
        eventIndex: this.drag.seg.eventIndex,
        edge: this.drag.edge,
        newTime: `${hh}:${mm}`,
      })
    },
    onResizeUp() {
      if (this.drag?.moved) this.suppressNextClick = true
      this.drag = null
      window.removeEventListener('pointermove', this.onResizeMove)
      window.removeEventListener('pointerup', this.onResizeUp)
    },

    partnerLabel(p) {  // eslint-disable-line no-unused-vars
      if (p === 'A') return this.partyAName || 'Party A'
      if (p === 'B') return this.partyBName || 'Party B'
      return ''
    },
    fmtDur: formatDurationShort,
    fmtPct(mins) { return formatPercent(mins, MIN_PER_WEEK) },
    weekPct(mins) { return (mins / MIN_PER_WEEK) * 100 },
    formatHourLabel(h) {
      if (h === 0) return '12:00'
      if (h < 12) return `${h}:00`
      if (h === 12) return '12:00'
      return `${h - 12}:00`
    },
    formatTime(hhmm) {
      const m = timeToMin(hhmm)
      const h24 = Math.floor(m / 60)
      const mm = m % 60
      const ampm = h24 < 12 ? 'am' : 'pm'
      const h12 = h24 === 0 ? 12 : (h24 > 12 ? h24 - 12 : h24)
      return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`
    },
    formatDay(d) { return DAY_LONG[d] || d },
    transitionSentence(e) {
      const t = e.transition || ''
      if (t.endsWith('_drops')) {
        const p = t.startsWith('A') ? this.partyAName || 'Party A' : this.partyBName || 'Party B'
        return `${p} drops off ${this.childrenLabel}`
      }
      if (t.endsWith('_picks')) {
        const p = t.startsWith('A') ? this.partyAName || 'Party A' : this.partyBName || 'Party B'
        return `${p} picks up ${this.childrenLabel}`
      }
      return ''
    },
    // The block whose end is the run's real end (and real transition).
    runLast(item) { return item.run ? item.run.last.e : item.e },
    // Displayed row (1-based) where the run's END is drawn. Sundays draw at
    // the head of the row after their data week.
    runEndVisualWeek(item) {
      const last = item.run ? item.run.last : { wi: item.wi, e: item.e }
      const row = (last.wi + (last.e.endWeekOffset || 0) + (last.e.endDay === 'sun' ? 1 : 0)) % this.totalWeeks
      return row + 1
    },
  },
}
</script>

<style scoped>
.week-block {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.week-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.week-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #34555f;
  letter-spacing: -0.005em;
}
.btn-add-inline {
  background: transparent;
  border: 1px dashed #cbd5e1;
  color: #557d89;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.3rem 0.7rem;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.btn-add-inline:hover {
  border-color: #3f6772;
  border-style: solid;
  background: #eef3f4;
  color: #34555f;
}

.week-grid {
  display: grid;
  grid-template-columns: 44px repeat(7, minmax(0, 1fr));
  grid-template-rows: 26px repeat(24, 22px);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}
.corner {
  grid-column: 1;
  grid-row: 1;
  background: #34555f;
}
.day-header {
  grid-row: 1;
  background: #34555f;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid rgba(255,255,255,0.12);
}
.hour-label {
  grid-column: 1;
  font-size: 0.66rem;
  color: #94a3b8;
  padding: 1px 4px 0 0;
  text-align: right;
  border-top: 1px solid #f1f5f9;
  font-variant-numeric: tabular-nums;
}
.day-cell {
  border-top: 1px solid #f1f5f9;
  border-left: 1px solid #f1f5f9;
  background: #fff;
  cursor: cell; /* hints that empty grid space is double-clickable */
}
.day-cell.even-row { background: #fafbfc; }

.event-block {
  align-self: stretch;
  justify-self: stretch;
  margin: 0 1px;
  border-radius: 3px;
  border: none;
  cursor: pointer;
  padding: 3px 5px;
  font-family: inherit;
  font-size: 0.72rem;
  text-align: left;
  overflow: hidden;
  transition: filter 0.15s;
  line-height: 1.15;
  color: #fff;
  min-height: 22px;
  position: relative;   /* anchor for drag handles */
}
.event-block:hover { filter: brightness(1.08); }
/* Multi-lane events (concurrent same-day segments) render side-by-side.
   The inline width/margin-left handles positioning; justify-self prevents
   the grid item from stretching to fill the column. */
.event-block.multi-lane {
  justify-self: start;
  margin-right: 0;
}

/* Drag handles at the top/bottom edges. Visible on hover, always present in the
   DOM so the pointer target is stable. Cursor changes to ns-resize to hint
   at drag-to-change-time. */
.drag-handle {
  position: absolute;
  left: 0;
  right: 0;
  height: 6px;
  cursor: ns-resize;
  z-index: 2;
  touch-action: none;
  background: transparent;
  transition: background 0.12s;
}
.drag-handle-top { top: 0; }
.drag-handle-bottom { bottom: 0; }
.event-block:hover .drag-handle { background: rgba(255, 255, 255, 0.35); }
.drag-handle:hover { background: rgba(255, 255, 255, 0.6) !important; }
.event-block.partner-a { background: #7797a1; color: #fff; }
.event-block.partner-b { background: #34555f; color: #fff; }
.event-label {
  font-weight: 500;
  font-size: 0.68rem;
  line-height: 1.2;
}

.week-dist {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  border-top: 1px dashed #e2e8f0;
  padding-top: 0.6rem;
}
.dist-bar {
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: #f1f5f9;
}
.dist-a {
  background: #7797a1;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.dist-b {
  background: #34555f;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.dist-gap {
  background: repeating-linear-gradient(45deg, #fcd34d, #fcd34d 4px, #fef3c7 4px, #fef3c7 8px);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.week-dist-legend {
  margin: 0;
  font-size: 0.76rem;
  color: #475569;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.week-dist-legend strong { color: #34555f; }
.dist-unassigned-note { color: #b45309; }

.event-summary {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.82rem;
  color: #1e293b;
  border-top: 1px dashed #e2e8f0;
  padding-top: 0.75rem;
}
.event-summary li {
  padding: 0.55rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fafbfc;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.summary-body { display: block; }
.transition-note {
  display: block;
  color: #64748b;
  font-size: 0.78rem;
}
.transition-note strong { color: #34555f; }
.empty-week {
  margin: 0;
  padding: 0.75rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.85rem;
  font-style: italic;
  border-top: 1px dashed #e2e8f0;
  padding-top: 0.75rem;
}

/* Print: compress each week so two weeks plus their transition summaries fit
   on one portrait letter page (pagination rules live in ParentingBuilder).
   Hour rows shrink from 22px to 11px; event blocks scale with them because
   they're placed on the same grid rows. */
@media print {
  .week-block {
    break-inside: avoid;
    page-break-inside: avoid;
    padding: 0.3rem 0.55rem;
    gap: 0.25rem;
    border-color: #cbd5e1;
    border-radius: 6px;
  }
  .week-title { font-size: 0.8rem; }
  .btn-add-inline { display: none !important; }
  .week-grid {
    /* The grid is the fixed cost of the sheet (24 rows x 2 weeks), so its
       row height is the lever that keeps a 2-week rotation on one page.
       ParentingBuilder sets --print-hour-row from how many timeblocks the
       rotation actually has: a sparse week stays airy, a 2/2/3 (six
       transition summaries) or a date-night split tightens to fit. */
    grid-template-rows: 13px repeat(24, var(--print-hour-row, 8.5px));
  }
  .hour-label { line-height: 1; }
  .day-header {
    font-size: 0.6rem;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .corner {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  /* Hour labels shrink with the rows, so a dense sheet stays legible
     instead of stacking 8px text into 6px rows. */
  .hour-label { font-size: var(--print-hour-font, 0.5rem); padding-top: 0; }
  .event-block {
    font-size: 0.53rem;
    padding: 1px 3px;
    min-height: 0;
    border-radius: 2px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .event-label { font-size: 0.53rem; }
  .drag-handle { display: none !important; }
  .event-summary {
    font-size: 0.68rem;
    gap: 0.18rem;
    padding-top: 0.22rem;
    line-height: 1.3;
  }
  .event-summary li {
    padding: 0.14rem 0.45rem;
    gap: 0.1rem;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .transition-note { font-size: 0.64rem; }
  .empty-week { display: none; }
  .week-dist { padding-top: 0.25rem; gap: 0.15rem; }
  .dist-bar { height: 6px; }
  .week-dist-legend { font-size: 0.66rem; }
}
</style>
